import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Search } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdDisplay from '../components/AdDisplay';
import ToolSelector from '../components/ToolSelector';

const FreezeDryDatabase = () => {
 const { API, user } = useContext(AuthContext);
 const navigate = useNavigate();
 const [products, setProducts] = useState([]);
 const [filteredProducts, setFilteredProducts] = useState([]);
 const [search, setSearch] = useState('');
 const [categoryFilter, setCategoryFilter] = useState('all');
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchProducts();
 }, []);

 useEffect(() => {
 filterProducts();
 }, [search, categoryFilter, products]);

 const fetchProducts = async () => {
 try {
 const response = await axios.get(`${API}/freeze-dry-database`);
 setProducts(response.data);
 } catch (error) {
 toast.error('Failed to load database');
 } finally {
 setLoading(false);
 }
 };

 const filterProducts = () => {
 let filtered = products;

 if (categoryFilter !== 'all') {
 filtered = filtered.filter(p => p.category.toLowerCase() === categoryFilter.toLowerCase());
 }

 if (search) {
 filtered = filtered.filter(p =>
 p.product_name.toLowerCase().includes(search.toLowerCase()) ||
 p.notes?.toLowerCase().includes(search.toLowerCase())
 );
 }

 setFilteredProducts(filtered);
 };

 const categories = ['All', 'Candy', 'Fruit', 'Vegetable', 'Meat', 'Seafood', 'Meal', 'Supplement', 'Other'];

 return (
 <div className="min-h-screen bg-background">
 <ToolSelector />
 <div className="bg-background border-b">
 <div className="container mx-auto px-4 py-4 flex items-center gap-4">
 <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
 <ArrowLeft className="w-5 h-5" />
 </Button>
 <h1 className="text-2xl font-bold">Freeze Dry Database</h1>
 </div>
 </div>

 <div className="container mx-auto px-4 py-8">
 <div className="grid lg:grid-cols-4 gap-6">
 {/* Main Content */}
 <div className="lg:col-span-3">
 <Card className="mb-6 shadow-lg">
 <CardHeader>
 <CardTitle>Product Database</CardTitle>
 <CardDescription>Search and filter freeze-drying data for various products</CardDescription>
 </CardHeader>
 <CardContent>
 <div className="flex flex-col md:flex-row gap-4">
 <div className="flex-1 relative">
 <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
 <Input
 placeholder="Search products..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 className="pl-10"
 data-testid="search-input"
 />
 </div>
 <Select value={categoryFilter} onValueChange={setCategoryFilter}>
 <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
 <SelectValue placeholder="Category" />
 </SelectTrigger>
 <SelectContent>
 {categories.map(cat => (
 <SelectItem key={cat} value={cat.toLowerCase()}>{cat}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </CardContent>
 </Card>

 {loading ? (
 <div className="text-center py-12">
 <p className="text-slate-500">Loading products...</p>
 </div>
 ) : filteredProducts.length === 0 ? (
 <Card className="text-center py-12">
 <p className="text-slate-500">No products found</p>
 </Card>
 ) : (
 <div className="grid gap-4">
 {filteredProducts.map((product) => (
 <Card key={product.id} className="shadow-md hover:shadow-lg transition-shadow" data-testid="product-card">
 <CardHeader>
 <div className="flex justify-between items-start">
 <div>
 <CardTitle className="text-xl">{product.product_name}</CardTitle>
 <CardDescription className="text-base">
 <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded mt-2">
 {product.category}
 </span>
 </CardDescription>
 </div>
 {product.water_content && (
 <div className="text-right">
 <p className="text-2xl font-bold text-primary">{product.water_content}%</p>
 <p className="text-xs text-slate-500">Water Content</p>
 </div>
 )}
 </div>
 </CardHeader>
 <CardContent>
 <div className="grid md:grid-cols-2 gap-4 text-sm">
 {product.pre_freeze_instructions && (
 <div>
 <p className="font-medium text-slate-700">Pre-Freeze:</p>
 <p className="text-slate-600">{product.pre_freeze_instructions}</p>
 </div>
 )}
 {product.temp_range && (
 <div>
 <p className="font-medium text-slate-700">Temperature:</p>
 <p className="text-slate-600">{product.temp_range}</p>
 </div>
 )}
 {product.pressure_range && (
 <div>
 <p className="font-medium text-slate-700">Pressure:</p>
 <p className="text-slate-600">{product.pressure_range}</p>
 </div>
 )}
 {product.primary_dry_time && (
 <div>
 <p className="font-medium text-slate-700">Primary Dry:</p>
 <p className="text-slate-600">{product.primary_dry_time}</p>
 </div>
 )}
 {product.secondary_dry_time && (
 <div>
 <p className="font-medium text-slate-700">Secondary Dry:</p>
 <p className="text-slate-600">{product.secondary_dry_time}</p>
 </div>
 )}
 {product.notes && (
 <div className="md:col-span-2">
 <p className="font-medium text-slate-700">Notes:</p>
 <p className="text-slate-600">{product.notes}</p>
 </div>
 )}
 </div>
 </CardContent>
 </Card>
 ))}
 </div>
 )}
 </div>

 {/* Sidebar with Ads */}
 <div className="lg:col-span-1 space-y-4">
 <AdDisplay location="database_sidebar" />
 </div>
 </div>
 </div>
 </div>
 );
};

export default FreezeDryDatabase;
